import { useEffect, useMemo, useState, ReactNode } from 'react'
import { useLocation } from 'react-router-dom'
import { toast } from 'react-toastify'
import Switch from 'react-switch'
import posthog from 'posthog-js'
import DotsLoading from 'components/loadings/dotsLoading'
import GatewayPicker from 'components/gatewayPicker'
import ManualPaymentCard from 'components/manualPayment'
import useAvailableGateways from 'utilities/hooks/useAvailableGateways'
import useGetInitPaymentOs from 'pages/wallet/hooks/useGetInitPaymentOs'
import { BoxRef } from 'services/easyPay/gateways'
import { faPlain, toman } from 'utilities/helper/format'
import useSetGatewayPayment from './hooks/useSetGatewayPeyment'
import CommissionHint from 'components/commissionHint'

/**
 * «تایید و پرداخت» — the receipt for a basket of payables.
 *
 * Rebuilt to be the same screen as the loan repayment receipt. They are the
 * same act — here is what you owe, pick how to pay, pay — and they used to be
 * two unrelated designs: this one was a torn-paper SVG hand, an absolutely
 * positioned fixed-height card and a bare row of bank logos, with a CTA
 * labelled «پرداخت درگاهی(جدید)» and a receipt line reading «پرداخت آسان در
 * گت وی». That is the "شکل و شمایلش هم یکی نیست" half of the report.
 *
 * The other half was the gateway list. This screen asked for gateways without
 * telling the backend which funds it was paying, so it was offered Pasargad on
 * funds Pasargad is not live on and the pay button 400'd; the loan screen did
 * name its fund and was offered کارت‌به‌کارت alone, which the old picker then
 * dropped. Both now go through `useAvailableGateways`, which asks per-fund.
 *
 * It also takes the wallet, which the whole «پرداخت آسان» flow never did —
 * the switch existed only on the pre-v2 screens this one replaced, and did not
 * work there either. Covered in full, the basket never reaches a bank; covered
 * in part, `use_wallet` nets the balance off and the card is charged the rest.
 */
export default function InitGatewayPayment() {
    const { state } = useLocation()
    const { mutate, isLoading: isPaying } = useSetGatewayPayment()

    const selected: any[] = useMemo(
        () => state?.toBePaidTransactions ?? [],
        [state]
    )

    // Ask about exactly the funds in the basket. `initiate_payment` requires
    // EVERY item's box to be allowed, so a gateway offered for a subset is a
    // gateway that will reject the payment.
    const boxes: BoxRef[] = useMemo(
        () =>
            selected.map((item) => ({
                type: item.box_type === 'csb' ? 'cbx' : 'mnp',
                id: Number(item.box_id),
            })),
        [selected]
    )

    const { gateways, selectedCode, setSelectedCode, hasNoGateway } =
        useAvailableGateways(boxes, { enabled: selected.length > 0 })

    const amount = Number(state?.total ?? 0)
    const commission = Number(state?.commission ?? 0)
    const count = selected.length
    const due = amount + commission

    // The wallet. Every v2 payment already settles THROUGH it — the bank tops
    // it up and each item is then paid out of it — so spending a balance that
    // is already there is the same act with the bank left out. This screen
    // never offered it, which is why a member with money in Hamyan still had
    // to reach for a card.
    const profile = JSON.parse(localStorage.getItem('profile') ?? 'null')
    const { data: walletData } = useGetInitPaymentOs(profile?.id)
    const balance = Number(walletData?.wallet_balance ?? 0)
    const [useWallet, setUseWallet] = useState(false)
    const hasWallet = balance > 0

    // The debit is amount + commission (`Transaction.total_amount`), so the
    // cover check has to be against the same figure — checking `amount` alone
    // is how the deposit screen used to hand `withdraw_wallet_balance` more
    // than it held and turn a payment into a 500.
    const walletCoversAll = useWallet && balance >= due
    const payable = useWallet ? Math.max(0, due - balance) : due
    // Covered in full ⇒ no bank, so nothing to pick and nothing to be blocked
    // by. A basket every gateway has turned down is still payable this way.
    const needsGateway = !walletCoversAll

    // A basket that reached the receipt and has nowhere to go. Counted because
    // it is a dead end we cannot see from the funnel: the payer neither pays
    // nor fails, they just stop.
    const hasManualFallback = !!selected[0]?.manual_payment?.phone
    useEffect(() => {
        if (!hasNoGateway) return
        posthog.capture('payment_no_gateway_available', {
            flow_type: 'gateway_payment',
            has_manual_fallback: hasManualFallback,
        })
    }, [hasNoGateway, hasManualFallback])

    const onPay = () => {
        if (needsGateway && !selectedCode) {
            toast.error('روش پرداخت را انتخاب کنید')
            return
        }
        posthog.capture('payables_gateway_selected', {
            gateway: walletCoversAll ? 'wallet' : selectedCode,
            items_count: count,
            total: due,
            use_wallet: useWallet,
            wallet_covers_all: walletCoversAll,
        })
        mutate({
            paymentItems: selected.map((item) => ({
                itemAmount: item.amount,
                itemType: item.type,
                itemId: item.id,
                box_id: item.box_id,
                commission: item.commission,
                description: item.description,
            })),
            // Deliberately absent when the wallet settles the whole basket:
            // sending one would make the backend run its per-gateway
            // kill-switch and box allowlist against a bank it is not going to
            // call, and refuse a payment that needs no bank at all.
            ...(needsGateway
                ? { gateway: { uniqueCode: selectedCode as string } }
                : {}),
            clientType: 'pwa',
            ...(useWallet ? { use_wallet: true } : {}),
        })
    }

    if (!state || count === 0) return <DotsLoading />

    return (
        <div className="px-4 pt-2 pb-8">
            <div className="bg-white border border-divider rounded-card shadow-card p-4">
                <p className="text-xs text-text-secondary text-center">
                    قابل پرداخت
                </p>
                <p className="mt-[2px] text-center text-[26px] font-bold text-heading leading-tight">
                    {toman(payable)}
                </p>

                <div className="mt-4 flex flex-col gap-2 border-t border-divider pt-3">
                    <Row
                        label={`مبلغ ${faPlain.format(count)} مورد`}
                        value={toman(amount)}
                    />
                    <Row label={<CommissionHint />} value={toman(commission)} />
                    {useWallet && (
                        <Row
                            label="از کیف پول"
                            value={toman(Math.min(balance, due))}
                        />
                    )}
                </div>

                {hasWallet && (
                    <div className="mt-3 flex items-center justify-between gap-3 border-t border-divider pt-3">
                        <div className="min-w-0">
                            <p className="text-sm text-text-body">
                                استفاده از کیف پول
                            </p>
                            <p className="text-[13px] text-text-secondary">
                                موجودی {toman(balance)}
                            </p>
                        </div>
                        <Switch
                            onChange={() => setUseWallet(!useWallet)}
                            checked={useWallet}
                            onColor="#75B844"
                            onHandleColor="#EAEEF0"
                            handleDiameter={22}
                            uncheckedIcon={false}
                            checkedIcon={false}
                            boxShadow="0px 1px 5px rgba(0, 0, 0, 0.6)"
                            activeBoxShadow="0px 1px 5px rgba(0, 0, 0, 0.6)"
                            height={26}
                            width={50}
                            className="react-switch shrink-0"
                            offColor="#B6BCC8"
                        />
                    </div>
                )}

                <div className="mt-3 border-t border-divider pt-3">
                    <p className="mb-2 text-[13px] text-text-secondary">
                        موارد این پرداخت
                    </p>
                    <div className="flex flex-col gap-1.5">
                        {selected.map((item) => (
                            <div
                                key={`${item.type}-${item.box_id}-${item.id}`}
                                className="flex items-baseline justify-between gap-3"
                            >
                                <span className="text-sm text-text-body shrink-0 truncate">
                                    {item.box_name}
                                </span>
                                <span className="text-sm font-medium text-text-body truncate">
                                    {toman(item.amount + item.commission)}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            <div className="mt-5">
                {walletCoversAll ? (
                    // No bank in this payment at all, so no picker and nothing
                    // that could turn it down.
                    <button
                        type="button"
                        onClick={onPay}
                        disabled={isPaying}
                        className="w-full rounded-full bg-green-1 py-2.5 text-[15px] font-bold text-white active:scale-[0.99] transition-transform motion-reduce:transition-none disabled:opacity-60"
                    >
                        {isPaying
                            ? 'در حال پرداخت…'
                            : `پرداخت ${toman(due)} از کیف پول`}
                    </button>
                ) : hasNoGateway ? (
                    // Reached when every fund in the basket is off every
                    // gateway. Before, this was a screen with a pay button
                    // that could only ever fail. A record-mode fund never gets
                    // here — its rows cannot be ticked — so the manual details
                    // are usually absent and the honest answer is that the
                    // online route is down, not that this fund never had one.
                    selected[0]?.manual_payment?.phone ? (
                        <ManualPaymentCard
                            info={selected[0].manual_payment}
                            boxName={selected[0].box_name}
                            source="payables_receipt"
                        />
                    ) : (
                        <div className="bg-white border border-divider rounded-card p-4 text-center">
                            <p className="text-sm font-bold text-heading">
                                پرداخت آنلاین در دسترس نیست
                            </p>
                            <p className="mt-1 text-[13px] leading-6 text-text-secondary">
                                در حال حاضر هیچ روش پرداختی برای این صندوق‌ها
                                فعال نیست. کمی بعد دوباره تلاش کنید یا با مدیر
                                صندوق هماهنگ کنید.
                            </p>
                        </div>
                    )
                ) : (
                    <>
                        <GatewayPicker
                            gateways={gateways}
                            selectedCode={selectedCode}
                            onSelect={setSelectedCode}
                        />
                        <button
                            type="button"
                            onClick={onPay}
                            disabled={isPaying}
                            className="mt-6 w-full rounded-full bg-green-1 py-2.5 text-[15px] font-bold text-white active:scale-[0.99] transition-transform motion-reduce:transition-none disabled:opacity-60"
                        >
                            {isPaying
                                ? 'در حال انتقال…'
                                : `پرداخت ${toman(payable)}`}
                        </button>
                    </>
                )}
            </div>
        </div>
    )
}

/**
 * Label on the right, value on the left — the page is already RTL.
 *
 * The label is a node, not a string, so a row can carry a disclosure under it
 * (see `CommissionHint`). That is why the label cell is a growable block
 * rather than a `shrink-0` inline span: a note wrapping to two lines has to
 * push the row taller, not shove the amount off the card.
 */
function Row({ label, value }: { label: ReactNode; value: string }) {
    return (
        <div className="flex items-start justify-between gap-3">
            <div className="text-sm text-text-secondary min-w-0">{label}</div>
            <span className="text-[15px] font-medium text-text-body truncate shrink-0">
                {value}
            </span>
        </div>
    )
}
